import { useState, useEffect } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useTrialStore } from '../store/trialStore';
import { useSubscriptionStore } from '../store/subscriptionStore';

/**
 * Popup that appears when a write attempt hits 423 LOCKED, or when the
 * ViewOnlyLockHero "Выбрать тариф" CTA dispatches the 'account-locked' event.
 *
 * Trial users haven't picked a paid tier yet, so we show a PLAN PICKER
 * (Starter / Professional / Business) with real prices and the stepped recovery
 * discount applied. The discount % is server-sourced (trialStore.recoveryDiscountPercent
 * from GET /api/trial/lock-state) so the price shown == the price charged; the
 * backend re-derives it authoritatively in POST /api/trial/recover.
 */
const PAID_PLAN_IDS = ['starter', 'professional', 'business'];

const RecoverySubscriptionModal = () => {
  const [open, setOpen] = useState(false);
  const { recover, recoveryDiscountPercent, fetchLockState } = useTrialStore();
  const { plans, fetchPlans } = useSubscriptionStore();
  const [pendingPlan, setPendingPlan] = useState(null);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      // Refresh plans + discount so the picker shows current prices.
      if (!plans || plans.length === 0) fetchPlans();
      fetchLockState();
    };
    window.addEventListener('account-locked', handler);
    return () => window.removeEventListener('account-locked', handler);
  }, [plans, fetchPlans, fetchLockState]);

  if (!open) return null;

  const paidPlans = (plans || []).filter(p => PAID_PLAN_IDS.includes(p.id));

  const fmt = (n) => `${Number(n).toLocaleString('ru-RU')}₽`;
  const discounted = (price) =>
    recoveryDiscountPercent > 0
      ? Math.round(price * (1 - recoveryDiscountPercent / 100))
      : price;

  const handlePick = async (planId) => {
    setPendingPlan(planId);
    const result = await recover(planId);
    if (result.success && result.data?.confirmationUrl) {
      window.location.href = result.data.confirmationUrl;
    } else {
      // Surface the failure — otherwise the button silently resets from
      // "Обработка…" back to the price and the user has no idea the payment
      // wasn't created (expired promo, network error, 5xx). This is the
      // money-screen; a dead button here = lost conversion.
      toast.error(result.error || 'Не удалось создать платёж. Попробуй ещё раз.');
      setPendingPlan(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="text-purple-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5"><LockClosedIcon className="w-3.5 h-3.5" />Аккаунт в режиме view-only</div>
        <h3 className="text-2xl font-bold text-white">Выбери тариф, чтобы вернуть доступ</h3>
        <p className="text-zinc-400 mt-2">
          Твой trial закончился. Все данные сохранены — выбери план, чтобы продолжить.
          {recoveryDiscountPercent > 0 && (
            <span className="text-lime-400 font-semibold"> Скидка −{recoveryDiscountPercent}% на первый месяц.</span>
          )}
        </p>

        <div className="mt-5 space-y-3">
          {paidPlans.length === 0 && (
            <div className="text-zinc-500 text-sm">Загрузка тарифов…</div>
          )}
          {paidPlans.map((plan) => {
            const final = discounted(plan.price);
            const hasDiscount = recoveryDiscountPercent > 0 && final !== plan.price;
            const busy = pendingPlan === plan.id;
            return (
              <button
                key={plan.id}
                onClick={() => handlePick(plan.id)}
                disabled={!!pendingPlan}
                className="w-full flex items-center justify-between gap-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-lime-400 rounded-lg px-4 py-3 text-left disabled:opacity-50 transition-colors"
              >
                <div>
                  <div className="text-white font-bold">{plan.name}</div>
                  <div className="text-xs text-zinc-400">
                    {plan.videoLimit?.toLocaleString('ru-RU')} видео/мес
                  </div>
                </div>
                <div className="text-right">
                  {hasDiscount && (
                    <div className="text-xs text-zinc-500 line-through">{fmt(plan.price)}/мес</div>
                  )}
                  <div className="text-lime-400 font-bold whitespace-nowrap">
                    {busy ? 'Обработка…' : `${fmt(final)}${hasDiscount ? ' первый мес' : '/мес'}`}
                  </div>
                  {hasDiscount && (
                    <div className="text-[11px] text-zinc-500">далее {fmt(plan.price)}/мес</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <button onClick={() => setOpen(false)} className="w-full text-zinc-500 hover:text-white text-sm mt-4">
          Позже
        </button>
      </div>
    </div>
  );
};

export default RecoverySubscriptionModal;
